import React, { useState } from "react";

import { PushpinOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Select } from '@shared/ui';
import { Row, Col, Button, Space, Popover, Typography, Form } from 'antd';
import { useTranslation } from 'react-i18next';

import { useRulesContext } from "./Context";

const { Text, Link } = Typography;

function Rule({ rule, updateRule, removeRule, onPinRule, options, rulePinned }) {
  const { t } = useTranslation();
  const [pinned, setPinned] = useState(rulePinned);
  const Component = rule.component;

  const handlePinClick = () => {
    onPinRule(rule.inputName, !pinned);
    setPinned(!pinned);
  };

  const handleRemoveClick = () => {
    removeRule(rule);
  };

  // 構建 Popover 內容
  const renderTooltipContent = () => {
    if (!rule.tooltip) return null;

    const { title, content, link } = rule.tooltip;

    return (
      <div style={{ maxWidth: 280 }}>
        {title && <Text strong style={{ display: 'block', marginBottom: 8 }}>{title}</Text>}
        {content && <Text style={{ display: 'block', marginBottom: link ? 8 : 0 }}>{content}</Text>}
        {link && (
          <Link href={link.url} target="_blank" rel="noopener noreferrer">
            {link.text}
          </Link>
        )}
      </div>
    );
  };

  return (
    <Row
      gutter={[12, 8]}
      style={{ marginBottom: '8px' }}
      align="middle"
    >
      <Col xs={24} sm={8} md={4}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <div style={{ width: 16, flexShrink: 0 }}>
            {rule.tooltip && (
              <Popover
                content={renderTooltipContent()}
                trigger="hover"
                placement="top"
              >
                <QuestionCircleOutlined style={{ color: '#8c8c8c', fontSize: 14, cursor: 'pointer' }} />
              </Popover>
            )}
          </div>
          <Select
            key={rule.inputName}
            options={options}
            style={{ flex: 1, minWidth: 0 }}
            value={{
              value: rule.inputName,
              label: rule.label,
            }}
            onChange={(selectedOption) => {
              updateRule(rule, selectedOption);
            }}
            placeholder={t('common.table.filters.select_filter')}
          />
        </div>
      </Col>
      <Col xs={24} sm={16} md={20}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Form.Item name={rule.inputName} style={{ marginBottom: 0 }} initialValue={rule.defaultValue}>
              <Component {...rule} />
            </Form.Item>
          </div>
          {!rule.fixed && (
            <Space style={{ flexShrink: 0, paddingTop: 4 }}>
              <Button
                type="text"
                size="small"
                icon={<PushpinOutlined />}
                onClick={handlePinClick}
                style={{
                  color: pinned ? '#1890ff' : '#8c8c8c'
                }}
                title={pinned ? t('common.table.filters.unpin_filter') : t('common.table.filters.pin_filter')}
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={handleRemoveClick}
                danger
                title={t('common.table.filters.remove_filter')}
              />
            </Space>
          )}
        </div>
      </Col>
    </Row>
  );
}

function RulesForm({ options,onPinRule,supportRules,pinnedRules }) {
  const { rulesInputNames, updateRule, removeRule } = useRulesContext();
  const fixedRuleNames = supportRules.filter(rule => rule.fixed).map(rule => rule.inputName)
  const selectedRules = [...new Set([...fixedRuleNames, ...rulesInputNames])]

  return (
    <>
      {selectedRules.map((ruleInputName) => {
        const rule = supportRules.find(rule=>rule.inputName === ruleInputName)
        if (!rule) return null
        return (
          <Rule key={rule.inputName}
            options={options}
            rule={rule}
            updateRule={updateRule}
            removeRule={removeRule}
            onPinRule={onPinRule}
            rulePinned={pinnedRules.includes(rule.inputName)}
          />
        );
      })}
    </>
  );
}

export default RulesForm;
